<template>
  <section
    class="recommendation"
    :class="{ 'has-highlight': highlight }"
  >
    <picture class="recommendation__picture">
      <img
        :src="imgSrc"
        width="480"
        height="240"
        :alt="imgAlt"
        class="recommendation__image"
      >
      <p
        class="tag"
        v-if="tag.length > 0"
        v-html="tag"
      />
    </picture>

    <div class="recommendation__content">
      <header class="recommendation__header">
        <headline level="3">
          {{ title }}
        </headline>
        <check-list
          v-if="list"
          :items="list"
          class="recommendation__check-list"
        />
      </header>

      <div
        v-for="ticket in tickets"
        :key="ticket.id"
        class="form-input form-input--inline form-input--inline-label recommendation__ticket form-input--neutral"
      >
        <label
          :for="ticket.id"
          class="form-input__label"
        >
          {{ ticketTitle(ticket) }} {{ price(getPrice(ticket)) }} €
        </label>
        <select
          :id="ticket.id"
          v-model="selection[ticket.id]"
          class="form-input__input form-input__input--select"
          @change="updateSelection(ticket.id)"
        >
          <option value="0">
            –
          </option>
          <option
            v-for="n in maxTickets"
            :key="n"
            :value="n"
            :selected="parseInt(selection[ticket.id]) === n"
          >
            {{ n }}
          </option>
        </select>
      </div>

      <footer class="recommendation__footer">
        <button-group align="end">
          <action
            :color="highlight ? 'primary-inverse-dark' : 'primary-inverse'"
            @click="displayInfo"
          >
            {{ $t('moreInfo') }}
            <icon
              type="chevron-right"
              :size="0.75"
            />
          </action>
          <action
            v-show="!highlight"
            :disabled="!anythingSelected"
            :color="highlight ? 'primary-inverse-dark' : 'primary-inverse'"
            @click="addToCart"
          >
            {{ $t('addToCart') }}
            <icon
              type="add"
              :size="0.75"
            />
          </action>
        </button-group>
      </footer>
    </div>
    <modal
      v-if="showInfo"
      size="medium"
      @close="hideInfo"
    >
      <p v-html="ticketDescription(tickets[0])">
        {{ ticketDescription(tickets[0]) }}
      </p>

      <template #footer>
        <action
          color="primary"
          @click="hideInfo"
        >
          Ok
        </action>
      </template>
    </modal>
  </section>
</template>

<script>
import action from './action.vue'
import buttonGroup from './buttonGroup.vue'
import checkList from './checkList.vue'
import headline from './headline.vue'
import icon from './icon.vue'
import priceHelper from '../functions/priceHelper'
import moveButton from '../functions/moveButton'
import modal from '../components/modal.vue'

export default {
  name: 'CRecommendation',

  components: {
    action,
    buttonGroup,
    checkList,
    headline,
    icon,
    modal
  },

  props: {
    title: {
      type: String,
      required: true
    },
    list: {
      type: Array,
      default: undefined
    },
    tag: {
      type: String,
      default: undefined
    },
    imgSrc: {
      type: String,
      required: true
    },
    imgAlt: {
      type: String,
      required: true
    },
    tickets: {
      type: Array,
      required: true
    },
    ticketType: {
      type: String,
      required: true
    },
    maxTickets: {
      type: Number,
      default: 14
    },
    highlight: Boolean
  },

  data () {
    return {
      selection: [],
      showInfo: false
    }
  },

  computed: {
    anythingSelected: function () {
      for (const ticketIndex in this.tickets) {
        const ticket = this.tickets[ticketIndex]
        if (this.selection[ticket.id] !== 0) {
          return true
        }
      }
      return false
    }
  },

  created () {
    for (const ticketIndex in this.tickets) {
      const ticket = this.tickets[ticketIndex]
      if (
        !(ticket.id in this.$store.state.voucherSelection) ||
        !this.$store.state.voucherSelection[ticket.id]
      ) {
        this.$store.state.voucherSelection[ticket.id] = 0
      }
      this.selection[ticket.id] = this.$store.state.voucherSelection[ticket.id]
    }
  },

  methods: {
    displayInfo: function () {
      let name = ''

      if (this.ticketType === 'hamburg') {
        name = 'Voucher Hamburg Dungeon'
      } else if (this.ticketType === 'hafenrundfahrt') {
        name = 'Voucher Hafenrundfahrt'
      } else if (this.ticketType === 'stadtrundfahrt') {
        name = 'Voucher Stadtrundfahrt'
      }

      const prod = {
        name: name,
        category: 'Tickets/Vouchers'
      }
      this.$tracking.eetCheckoutUpsellViewDetail(prod)

      this.showInfo = true
    },
    hideInfo: function () {
      this.showInfo = false
    },

    ticketTitle: function (ticket) {
      return (this.$i18n.locale === 'de') ? ticket.text : ticket.text_en
    },
    ticketDescription: function (ticket) {
      return (this.$i18n.locale === 'de') ? ticket.description.replace(/\\/g, '') : ticket.description_en.replace(/\\/g, '')
    },

    updateSelection (ticketId) {
      this.$store.commit('updateVoucherSelection', { id: ticketId, value: this.selection[ticketId] })
    },
    getPrice (ticket) {
      return ticket.price
    },
    price (price) {
      return priceHelper.format((price / 100), this.$i18n.locale)
    },
    addToCart (event) {
      // EET
      const products = []
      for (const sid in this.selection) {
        const amount = this.selection[sid]
        if (amount === 0) { continue }

        let ticketData = null

        for (const i in this.tickets) {
          if (!(i in this.tickets)) { continue }
          if (this.tickets[i].id === parseInt(sid)) {
            ticketData = this.tickets[i]
            break
          }
        }

        if (ticketData === null) { continue }

        let name = ''
        let tid = ''

        if (this.ticketType === 'hamburg') {
          name = 'Voucher Hamburg Dungeon'
          tid = 'wv.du-'
        } else if (this.ticketType === 'hafenrundfahrt') {
          name = 'Voucher Hafenrundfahrt'
          tid = 'wv.hr-'
        } else if (this.ticketType === 'stadtrundfahrt') {
          name = 'Voucher Stadtrundfahrt'
          tid = 'wv.sr-'
        }

        const prod = {
          name: name,
          variant: ticketData.text,
          category: 'Tickets/Vouchers',
          tid: tid + ticketData.id,
          price: ticketData.price,
          taxRate: ticketData.tax_rate * 100,
          quantity: amount
        }

        products.push(prod)
      }

      this.$tracking.eetCheckoutUpsellAddToCart(products)
      this.$store.dispatch('addVouchersToCart', this.selection)
      moveButton(event, document.querySelector('#cart'))
    }
  }
}
</script>

<i18n locale="en">
{
  "moreInfo": "More Info",
  "addToCart": "Add to shopping cart"
}
</i18n>

<i18n locale="de">
{
  "moreInfo": "Mehr Informationen",
  "addToCart": "In den Warenkorb"
}
</i18n>

<style lang="scss">
@import "../css/variables";
@import "../css/functions";

.recommendation {
  display: flex;
  flex-direction: column;
  margin-bottom: $gutter-xs;

  @media (min-width: $breakpoint-lg) {
    flex-direction: row;
  }
}

.recommendation__picture {
  display: block;
  position: relative;
  height: 10em;

  @media (min-width: $breakpoint-lg) {
    width: 40%;
    height: auto;
  }
}

.recommendation__image {
  position: absolute;
  width: 100%;
  height: 100%;

  object-fit: cover;
}

.recommendation__content {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: $gutter-sm;

  color: #fff;
  background-color: $color-primary-dark;

  @media (min-width: $breakpoint-lg) {
    align-items: flex-end;
    padding: $gutter-md;
  }

  .has-highlight & {
    background-color: $color-primary;
  }
}

.recommendation__header {
  align-self: flex-start;
  margin-bottom: $gutter-sm;

  @media (min-width: $breakpoint-md) {
    margin-bottom: $gutter-md;
  }
}

.recommendation__check-list {
  margin-top: $gutter-xs;
  font-size: 0.875em;
}

.recommendation__ticket {
  margin-top: $gutter-xs;

  @media (min-width: $breakpoint-md) {
    text-align: right;
  }
}

.recommendation__footer {
  margin-top: $gutter-sm;
}
</style>
